/**
 * Event Field Check-in service — authenticated, staff-facing.
 *
 * Backs the /event-field-checkin mobile page: list selectable events, look up a person's
 * member status (with L4 conflict detection), register a walk-in (member-validate → voucher,
 * guests, lead-only), and reuse the existing check-in-by-code RPCs for the scan path.
 */
import { supabase } from "./supabase";
export {
  getAttendeeByCheckinCode,
  checkinAttendeeByCode,
} from "./events.service";

const FN = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/event-walkin-register`;
const BOOTH_SCAN_FN = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/booth-scan-checkin`;

export interface BoothScanResult {
  kind?: "token" | "admission" | "voucher";
  admitted?: boolean;
  already_checked_in?: boolean;
  holder?: string | null;
  attendee_id?: string | null;
  voucher_id?: string | null;
  message?: string | null;
  error?: string;
  found?: boolean;
  // Booth #3: TK- ticket(s) issued/confirmed for a fresh CI check-in at a ticket event, shown at the booth.
  tickets?: Array<{ code: string; qrUrl: string | null }>;
}

/**
 * One-tap booth scan → admit for ANY scanned code (TK- Event Ticket, CI- admission,
 * or voucher), resolved and admitted server-side in a single call.
 */
export async function boothScanCheckin(
  code: string,
  eventId: string,
): Promise<{ status: number; ok: boolean; result: BoothScanResult }> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    throw new Error("Not authenticated");
  }
  const res = await fetch(BOOTH_SCAN_FN, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ code, event_id: eventId }),
  });
  const result = (await res.json().catch(() => ({}))) as BoothScanResult;
  return { status: res.status, ok: res.ok, result };
}

async function authedPost(
  body: unknown,
): Promise<{ status: number; ok: boolean; json: any }> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    throw new Error("Not authenticated");
  }
  const res = await fetch(FN, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  return { status: res.status, ok: res.ok, json };
}

export interface FieldCheckinEvent {
  id: string;
  title: string;
  start_time: string | null;
  location_name: string | null;
  status: string | null;
  capacity: number | null;
  max_attendees: number | null;
  current_attendees_count: number | null;
  // #6: the host retailer (organization) + location, so the picker can be identified/searched by
  // retailer + location rather than the internal event title the sales staff use.
  organization_name?: string | null;
  location_city?: string | null;
  location_state?: string | null;
}

/**
 * Events a field rep can check people into: TODAY only (Central day), not cancelled/completed,
 * with the host retailer (organization) + location for retailer/location search (#6). Backed by
 * the staff-gated list_booth_events RPC so the Central-day boundary + joins live server-side.
 */
export async function listFieldCheckinEvents(): Promise<FieldCheckinEvent[]> {
  const { data, error } = await supabase.rpc("list_booth_events", {
    p_date: null,
  });
  if (error) {
    throw error;
  }
  return (data || []) as FieldCheckinEvent[];
}

export interface PersonLookup {
  found: boolean;
  conflict: boolean;
  hasVoucher: boolean;
  // The event issues Event Tickets (an active issue_as_ticket offer): default the issue toggle ON for
  // everyone (not just members) and relabel "voucher" → "ticket".
  hasTicket?: boolean;
  contactId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  membershipId?: string | null;
  isMember?: boolean;
  // 1040: isMember is true because Zuora confirmed the membership live just now (the CRM record has not caught up).
  memberVerifiedLive?: boolean;
  emailContact?: { id: string; name: string; isMember: boolean };
  memberIdContact?: { id: string; name: string; isMember: boolean };
}

/** Resolve member status + L4 conflict for the entered email/member id, scoped to an event. */
export async function lookupPerson(args: {
  eventId: string;
  email?: string;
  memberId?: string;
}): Promise<PersonLookup> {
  const { json } = await authedPost({
    action: "lookup",
    eventId: args.eventId,
    email: args.email?.trim() || undefined,
    memberId: args.memberId?.trim() || undefined,
  });
  return json as PersonLookup;
}

export interface CapturedGuest {
  firstName?: string;
  lastName?: string;
  email: string;
  issueVoucher?: boolean;
}

export interface WalkInInput {
  eventId: string;
  mode: "checkin" | "lead";
  registrant: {
    firstName: string;
    lastName: string;
    email: string;
    memberId?: string;
    phone?: string;
    // ZIP disambiguates a member household when only an email is given (dup-vector Option A). Optional:
    // the resolver prompts for it (member_needs_disambiguation) only when a member claim needs it.
    zip?: string;
  };
  issueVoucher?: boolean;
  guestCount?: number;
  capturedGuests?: CapturedGuest[];
  override?: boolean;
  // Option A (event-page operator panel): issue ONLY the event's configured ticket offer
  // and preflight that config. Absent for the standalone field-checkin page (legacy: issue
  // every active offer the rep opted into).
  configuredTicketOnly?: boolean;
  // Batch #3: the non-member/guest walk-in registrant checked the required SMS opt-in. The edge fn
  // records affirmative consent on the registrant's contact, and only when they're not a member.
  messagingConsent?: boolean;
  // F2: the OWNER CONTACT ID the operator confirmed is the same person (bound; server revalidates it
  // against the resolved owner so editing the email after the prompt invalidates a stale confirm).
  confirmEmailOwnerId?: string;
  // Operator picked an existing ACTIVE MEMBER from the "Find an active member" search. When set, the
  // edge fn forks early: it re-reads THIS contact server-side for the authoritative identity and ignores
  // the typed registrant fields entirely (they cannot override or redirect the selection).
  selectedContactId?: string;
}

/** One row from the event-scoped active-member search (disambiguation fields only — never full rows). */
export interface EventActiveMember {
  contact_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  membership_id: string | null;
  phone: string | null;
  postal_code: string | null;
  is_primary_member: boolean | null;
}

export interface EventMemberSearchResult {
  ok: boolean;
  members: EventActiveMember[];
  /** 'not_authorized' | 'not_authenticated' | 'too_broad' | 'unknown' — for a friendly UI state. */
  error?: "not_authorized" | "not_authenticated" | "too_broad" | "unknown";
  message?: string;
}

/**
 * Search ACTIVE members for an event the caller can operate. Direct, self-authorizing RPC (mirrors the
 * Contacts page's search_contacts_page): the function checks auth.uid() + can_operate_event_reward and
 * returns only disambiguation fields. Explicit-submit only — call this from the SearchBar submit, never
 * on keystroke. Hygiene: never log the raw query or the returned rows.
 */
export async function searchEventActiveMembers(
  eventId: string,
  query: string,
): Promise<EventMemberSearchResult> {
  const q = (query ?? "").trim();
  if (!q) {
    return { ok: true, members: [] };
  }
  const { data, error } = await supabase.rpc("search_event_active_members", {
    p_event_id: eventId,
    p_query: q,
    p_limit: 20,
  });
  if (error) {
    const code = (error as { code?: string }).code;
    if (code === "42501") {
      return {
        ok: false,
        members: [],
        error: "not_authorized",
        message: "You don't have permission to search members for this event.",
      };
    }
    if (code === "28000") {
      return {
        ok: false,
        members: [],
        error: "not_authenticated",
        message: "Your session expired — please sign in again.",
      };
    }
    if (code === "22023") {
      // Empty/whitespace/oversized fails closed server-side — treat as no results, not an error banner.
      return { ok: true, members: [], error: "too_broad" };
    }
    return {
      ok: false,
      members: [],
      error: "unknown",
      message: "Couldn't run the search right now. Please try again.",
    };
  }
  return { ok: true, members: (data ?? []) as EventActiveMember[] };
}

export type WalkInRewardState = "assigned" | "pending" | "void" | "none";

export interface WalkInResult {
  status: number;
  ok: boolean;
  capacityBlocked: boolean;
  lookupConflict: boolean;
  // F2: the typed email is on file for a blank/different-named contact → the operator must confirm it's
  // the same person before we reuse it (resubmit with confirmEmailOwner). onFileName = the on-file name
  // to show, or null when the contact has no name recorded.
  emailNeedsConfirmation: boolean;
  onFileName?: string | null;
  // F2: the contact id the confirm banner is bound to — echoed back on Yes so the server honors the
  // confirm only for this exact owner.
  ownerContactId?: string | null;
  maxCapacity?: number;
  usedPeople?: number;
  partySize?: number;
  attendeeId?: string;
  registrantIsMember?: boolean;
  // 1040: the member status came from a live Zuora check for this check-in only.
  memberVerifiedLive?: boolean;
  alreadyAttended?: boolean;
  overrodeCapacity?: boolean;
  newMaxAttendees?: number | null;
  vouchersIssued?: number;
  leadOnly?: boolean;
  tickets?: Array<{ code: string; qrUrl: string | null }>;
  // F2: the registrant's FINAL reward after escalation — 'assigned' = real member/guest
  // award applied; 'pending' = placeholder not yet finalized (must NOT read as issued);
  // 'void'/'none' = no live reward. rewardOffer is the assigned offer's display name.
  rewardState?: WalkInRewardState;
  rewardOffer?: string | null;
  // SMS consent audit (HIGH 2): the server's honest answer about whether the required opt-in was
  // actually written as evidence. `false` means the check-in succeeded but NO consent record exists —
  // the operator must be told, because the box was ticked and the person believes they opted in.
  // `undefined` = consent was not requested on this submission.
  consentRecorded?: boolean;
  guests?: {
    email: string;
    attendeeId: string | null;
    voucher: boolean;
    tickets?: Array<{ code: string; qrUrl: string | null }>;
    rewardState?: WalkInRewardState;
    rewardOffer?: string | null;
  }[];
  // Non-fatal issues from the reward/guest path (e.g. "guest x: contact failed",
  // "$0 ticket — no reward issued"). The backend always returns these; the operator
  // must see them so a silently-skipped reward isn't mistaken for a full success.
  warnings?: string[];
  error?: string;
  message?: string;
}

/** Register a walk-in. A 409 capacity is surfaced (not thrown) so the UI can override. */
export async function registerWalkIn(
  input: WalkInInput,
): Promise<WalkInResult> {
  const { status, ok, json } = await authedPost({ ...input });
  return {
    status,
    ok,
    capacityBlocked: status === 409 && json?.error === "capacity",
    lookupConflict: status === 409 && json?.error === "lookup_conflict",
    emailNeedsConfirmation:
      status === 409 && json?.error === "email_needs_confirmation",
    onFileName: json?.onFileName ?? null,
    ownerContactId: json?.ownerContactId ?? null,
    maxCapacity: json?.maxCapacity,
    usedPeople: json?.usedPeople,
    partySize: json?.partySize,
    attendeeId: json?.attendeeId,
    registrantIsMember: json?.registrantIsMember,
    memberVerifiedLive: json?.memberVerifiedLive === true,
    alreadyAttended: json?.alreadyAttended,
    overrodeCapacity: json?.overrodeCapacity,
    newMaxAttendees: json?.newMaxAttendees,
    vouchersIssued: json?.vouchersIssued,
    leadOnly: json?.leadOnly,
    tickets: json?.tickets,
    rewardState: json?.rewardState,
    rewardOffer: json?.rewardOffer,
    consentRecorded:
      typeof json?.consentRecorded === "boolean"
        ? json.consentRecorded
        : undefined,
    guests: json?.guests,
    warnings: Array.isArray(json?.warnings) ? json.warnings : undefined,
    error: json?.error,
    message: json?.message,
  };
}
